import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ConfigCapos } from '@app/interfaces/config-capos.interface';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-formulario-dinamico',
    templateUrl: './formulario-dinamico.component.html',
    styleUrls: ['./formulario-dinamico.component.scss'],
    standalone: false
})

// Para este formulario deberias pasar un array con los tipos de input y los steps que se quieren en otro array la forma del array sera la interface de ConfigCapos[] cualuqier cosa consultra Angelo
export class FormularioDinamicoComponent implements OnInit {
    @Input() config: ConfigCapos[] = []; //configuracion de los campos
    @Input() stepLabels: { label: string; columnas: number }[] = []; //steps y columnas
    @Input() activeStep: number = 0; //elementos por steps
    @Input() readOnly: boolean = false; // si true, todos los campos estarán en modo solo lectura
    @Input() flattenSteps: boolean = false; // si true, ignorar steps y mostrar todo en un único form
    @Input() showSteps?: boolean;
    @Output() activeStepChange = new EventEmitter<number>();
    @Output() submitForm = new EventEmitter<{ [key: string]: any }>();
    asyncOptions: { [key: string]: any[] } = {}; //para conceccion con el back
    @Output() fieldChange = new EventEmitter<{ [key: string]: any }>();
    @Output() onFieldBlur = new EventEmitter<{ [key: string]: any }>();


    onFieldChange(name: string, value: any) {
        this.formData[name] = value;
        
        // Buscar el campo en la configuración para ejecutar onChange si existe
        const field = this.config.find(f => f.name === name);
        if (field && field.onChange) {
            field.onChange(value, this.formData);
        }
        
        this.fieldChange.emit(this.formData);
    }

    searchOptions(fieldName: string, fn: (query: string) => Observable<any[]>, query: string) {
        const q = (query || '').trim();
        
        // Si el query está vacío y ya tenemos opciones iniciales, mantenerlas
        if (q.length < 1) {
            const field = this.config.find(f => f.name === fieldName);
            if (field && field.options && field.options.length > 0) {
                // Mantener las opciones iniciales si no hay búsqueda
                this.asyncOptions[fieldName] = field.options;
                return;
            } else {
                this.asyncOptions[fieldName] = [];
                return;
            }
        }
        
        fn(q).subscribe((options) => {
            // Filtro defensivo por si el backend no filtra correctamente
            const lower = q.toLowerCase();
            const filtered = (options || []).filter((opt: any) => {
                if (opt == null) return false;
                if (typeof opt === 'string') return opt.toLowerCase().includes(lower);
                const display = (opt.nombreCompleto ?? opt.label ?? opt.name ?? '').toString();
                return display.toLowerCase().includes(lower);
            });
            this.asyncOptions[fieldName] = filtered;
        });
    }

    stepItems: { label: string; columnas: number }[] = [];
    hasSteps = false;

    formData: { [key: string]: any } = {};

    ngOnInit(): void {
        // Inicializar campos
        this.config.forEach((field) => {
            // Para dropdowns con asyncOptionsFn que tienen defaultValue y options (campos preconfigurados como coordinador)
            if (field.type === 'dropdown' && field.asyncOptionsFn && field.defaultValue && field.options && field.options.length > 0) {
                // Buscar el objeto completo en las opciones usando el defaultValue como ID
                const selectedOption = field.options.find(opt => 
                    opt[field.optionValue || 'id'] === field.defaultValue
                );
                this.formData[field.name] = selectedOption || field.defaultValue;
                this.asyncOptions[field.name] = field.options;
                
             
            } else if (field.type === 'dropdown' && field.asyncOptionsFn && field.defaultValue && !field.options) {
                // Para campos readonly que tienen defaultValue pero no options, llamar asyncOptionsFn sin query
                this.formData[field.name] = field.defaultValue;
                field.asyncOptionsFn('').subscribe(options => {
                    this.asyncOptions[field.name] = options;
                    // Buscar el objeto completo después de cargar las opciones
                    const selectedOption = options.find(opt => 
                        opt[field.optionValue || 'id'] === field.defaultValue
                    );
                    if (selectedOption) {
                        this.formData[field.name] = selectedOption;
                    }
                   
                });
            } else {
                this.formData[field.name] = field.defaultValue ?? '';
            }
        });

        // Si se indicó modo solo lectura global, marcar campos como readonly
        if (this.readOnly) {
            this.config.forEach((field) => ((field as any).isReadOnly = true));
        }

        // Detectar pasos (permitimos forzar vista plana con flattenSteps)
        // showSteps === false fuerza ocultar la navegación por pasos.
        const shouldShowSteps = this.showSteps !== false;

        if (this.flattenSteps || !shouldShowSteps) {
            // Forzamos vista plana: todos los campos en un único paso
            this.hasSteps = false;
            this.stepItems = [{ label: 'Formulario', columnas: 3 }];
            this.config.forEach((c) => (c.stepIndex = 0));
        } else {
            // Comportamiento original: detectamos si la config define pasos
            this.hasSteps = this.config.some((c) => (c.stepIndex ?? 0) > 0);

            if (this.hasSteps) {
                const maxStep = Math.max(...this.config.map((c) => c.stepIndex ?? 0));
                for (let i = 0; i <= maxStep; i++) {
                    const item = this.stepLabels[i];
                    const label = item?.label ?? `Paso ${i + 1}`;
                    const columnas = item?.columnas ?? 3;
                    this.stepItems.push({ label, columnas });
                }
            } else {
                this.stepItems = [{ label: 'Formulario', columnas: 3 }];
                this.config.forEach((c) => (c.stepIndex = 0));
            }
        }

        // if ('fecha_solicitud' in this.formData) {
        //     this.formData['fecha_solicitud'] = new Date();
        // }
    }
    getColumnaSteps(): number {
        if (!this.hasSteps || !this.stepItems.length) return 3;
        return this.stepItems[this.activeStep]?.columnas || 3;
    }

    getFieldsByStep(step: number): ConfigCapos[] {
        return this.config.filter((c) => c.stepIndex === step);
    }

    nextStep() {
        if (this.activeStep < this.stepItems.length - 1) {
            this.activeStepChange.emit(this.activeStep + 1);
        }
    }

    prevStep() {
        if (this.activeStep > 0) {
            this.activeStepChange.emit(this.activeStep - 1);
        }
    }

    onSubmit() {
        this.submitForm.emit(this.formData);
    }

    isStepValid(step: number): boolean {
        const fields = this.getFieldsByStep(step);
        return fields.every((field) => {
            const visible = !field.showWhen || field.showWhen(this.formData);
            if (!visible) return true; 
            if (!field.required) return true;
            const v = this.formData[field.name];
            return v !== null && v !== undefined && v !== '';
        });
    }
    onFieldSelect(fieldName: string, event: any) {
        const selected = event?.value;
        this.formData[fieldName] = selected;
        
        // Buscar el campo en la configuración para ejecutar onChange si existe
        const field = this.config.find(f => f.name === fieldName);
        if (field && field.onChange) {
            field.onChange(selected, this.formData);
        }
        
        // Lógica específica para operario (mantener compatibilidad)
        if (fieldName === 'operario_id' && selected) {
            this.formData['sector_nombre'] = selected.sector?.nombre || null;
            this.formData['sector_id'] = selected.sector?.id || null;
            this.formData['distrito_id'] = selected.distrito?.nombre || null;
        }
        
        this.fieldChange.emit(this.formData);
    }
    onFieldBlurEvent(fieldName?: string) {
        if (fieldName) {
            const field = this.config.find(f => f.name === fieldName);
            if (field?.onBlur) {
                try { field.onBlur(this.formData[fieldName], this.formData); } catch (e) { console.warn('Error onBlur', fieldName, e); }
            }
        } else {
            // fallback (legacy usage)
            this.config.forEach(f => {
                if (f.onBlur) {
                    try { f.onBlur(this.formData[f.name], this.formData); } catch (e) { console.warn('Error onBlur', f.name, e); }
                }
            });
        }
        this.fieldChange.emit(this.formData);
        this.onFieldBlur.emit(this.formData);
    }

    /**
     * Calcula las fechas disponibles basadas en una fecha de inicio y el número de días disponibles
     * @param startDate - Fecha de inicio
     * @param availableDays - Número de días disponibles
     * @returns Array de fechas disponibles
     */
    calculateAvailableDates(startDate: Date, availableDays: number): Date[] {
        if (!startDate || availableDays <= 0) return [];
        
        const dates: Date[] = [];
        const start = new Date(startDate);
        
        for (let i = 0; i < availableDays; i++) {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            dates.push(date);
        }
        
        return dates;
    }

    /**
     * Calcula las fechas que deben estar deshabilitadas (todas excepto las disponibles)
     * @param startDate - Fecha de inicio
     * @param availableDays - Número de días disponibles
     * @param rangeMonths - Número de meses a considerar para el rango (por defecto 3)
     * @returns Array de fechas deshabilitadas
     */
    calculateDisabledDates(startDate: Date, availableDays: number, rangeMonths: number = 3): Date[] {
        if (!startDate || availableDays <= 0) return [];
        
        const availableDates = this.calculateAvailableDates(startDate, availableDays);
        const disabledDates: Date[] = [];
        
        // Crear un Set con las fechas disponibles para búsqueda rápida
        const availableDateStrings = new Set(
            availableDates.map(date => date.toDateString())
        );
        
        // Calcular rango de fechas a considerar
        const startRange = new Date(startDate);
        const endRange = new Date(startDate);
        endRange.setMonth(endRange.getMonth() + rangeMonths);
        
        // Iterar por cada día en el rango y agregar a disabled si no está en available
        const currentDate = new Date(startRange);
        while (currentDate <= endRange) {
            if (!availableDateStrings.has(currentDate.toDateString())) {
                disabledDates.push(new Date(currentDate));
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return disabledDates;
    }

    /**
     * Actualiza las restricciones de fecha para un campo específico
     * @param fieldName - Nombre del campo a actualizar
     * @param startDate - Fecha de inicio
     * @param availableDays - Días disponibles
     */
    updateDateRestrictions(fieldName: string, startDate: Date, availableDays: number): void {
        const field = this.config.find(f => f.name === fieldName);
        if (!field || field.type !== 'date') return;

        if (!startDate || availableDays <= 0) {
            // Si no hay fecha de inicio o días disponibles, deshabilitar el campo
            field.isReadOnly = true;
            field.disabledDates = [];
            field.minDate = undefined;
            field.maxDate = undefined;
            return;
        }

        // Habilitar el campo
        field.isReadOnly = false;
        
        // Calcular fecha mínima y máxima
        field.minDate = new Date(startDate);
        const maxDate = new Date(startDate);
        maxDate.setDate(maxDate.getDate() + availableDays - 1);
        field.maxDate = maxDate;
        
        // Calcular fechas deshabilitadas (todas las fechas fuera del rango permitido)
        field.disabledDates = this.calculateDisabledDates(startDate, availableDays);
        
    }
}
